/* eslint-disable camelcase */
import moment from 'moment';
import { ensureAbsoluteImageUrl } from '../../shared/imageUrlHelper';
import normalizeWlsRefundPolicy from './refund-policy';

const valueOrFallback = (value, fallback) =>
  value === undefined || value === null ? fallback : value;

const bookingRangeForDates = booking => {
  if (!booking || !booking.check_in || !booking.check_out) return [];
  const day = moment(booking.check_in, 'YYYY-MM-DD');
  const end = moment(booking.check_out, 'YYYY-MM-DD');
  const range = [];
  while (day.isSameOrBefore(end, 'day')) {
    range.push(day.format('YYYY-MM-DD'));
    day.add(1, 'day');
  }
  return range;
};

const normalizeFeaturedImage = featuredImage => {
  if (!featuredImage) return null;
  const imageUrl =
    (featuredImage.image &&
      featuredImage.image.small &&
      featuredImage.image.small.url) ||
    (featuredImage.image && featuredImage.image.url) ||
    featuredImage.url;
  if (!imageUrl) return null;
  const absoluteUrl = ensureAbsoluteImageUrl(imageUrl);
  const image = featuredImage.image || {};
  const smallImage = image.small || {};
  return {
    ...featuredImage,
    url: ensureAbsoluteImageUrl(featuredImage.url || absoluteUrl),
    image: {
      ...image,
      url: ensureAbsoluteImageUrl(image.url || absoluteUrl),
      small: {
        ...smallImage,
        url: ensureAbsoluteImageUrl(smallImage.url || absoluteUrl)
      }
    }
  };
};

const normalizeWlsBookingResponse = (data, brand) => {
  if (!data || typeof data !== 'object') return data;

  const bookingRange =
    data.booking && Array.isArray(data.booking.booking_range)
      ? data.booking.booking_range
      : bookingRangeForDates(data.booking);
  const nights = valueOrFallback(
    data.nights,
    bookingRange.length > 0 ? Math.max(bookingRange.length - 1, 0) : 0
  );
  const location = data.location
    ? {
        ...data.location,
        geo_latitude: valueOrFallback(
          data.location.geo_latitude,
          valueOrFallback(data.location.lat, null)
        ),
        geo_longitude: valueOrFallback(
          data.location.geo_longitude,
          valueOrFallback(data.location.lng, null)
        )
      }
    : data.location;
  const availability = data.availability
    ? {
        ...data.availability,
        default_time_check_in:
          data.availability.default_time_check_in ||
          data.availability.check_in_time ||
          '',
        default_time_check_out:
          data.availability.default_time_check_out ||
          data.availability.check_out_time ||
          ''
      }
    : data.availability;
  const booking = data.booking
    ? {
        ...data.booking,
        booking_range: bookingRange,
        check_in_time:
          data.booking.check_in_time ||
          (data.availability && data.availability.check_in_time) ||
          '',
        check_out_time:
          data.booking.check_out_time ||
          (data.availability && data.availability.check_out_time) ||
          '',
        notes: Array.isArray(data.booking.notes) ? data.booking.notes : []
      }
    : data.booking;
  const charges = Array.isArray(data.charges)
    ? data.charges.map(charge => ({
        ...charge,
        currency: charge.currency || (brand && brand.currency)
      }))
    : [];
  const pricing = data.pricing
    ? {
        ...data.pricing,
        discount: Array.isArray(data.pricing.discount)
          ? data.pricing.discount
          : [],
        tax_line_items: Array.isArray(data.pricing.tax_line_items)
          ? data.pricing.tax_line_items
          : [],
        fees: Array.isArray(data.pricing.fees)
          ? data.pricing.fees.map(fee => ({
              ...fee,
              additional_data: {
                included_in_base_rent: false,
                ...fee.additional_data
              }
            }))
          : []
      }
    : data.pricing;
  const balanceDueOn =
    data.payment_balance_due_on || data.bookingPaymentBalanceDueOn || '';
  const balanceDueDate =
    data.payment_balance_due_date || data.bookingPaymentBalanceDueDate || '';
  const autoCollected = Boolean(
    data.payment_auto_collected || data.bookingPaymentAutoCollected
  );

  return {
    ...data,
    availability,
    booking,
    charges,
    featured_image: normalizeFeaturedImage(data.featured_image),
    listing: data.listing
      ? {
          ...normalizeWlsRefundPolicy(data.listing),
          currency: data.listing.currency || (brand && brand.currency)
        }
      : data.listing,
    location,
    nights,
    // The receipt endpoint names these snake_case, the payment endpoint
    // camelCase. Populate both so either page can read either name.
    bookingPaymentAutoCollected: autoCollected,
    bookingPaymentBalanceDueDate: balanceDueDate,
    bookingPaymentBalanceDueOn: balanceDueOn,
    payment_auto_collected: autoCollected,
    payment_balance_due_date: balanceDueDate,
    payment_balance_due_on: balanceDueOn,
    pricing,
    // Left undefined when the endpoint has no refunds list so the pages can
    // tell "no refunds" apart from "not provided" (see refundTotal).
    refunds: Array.isArray(data.refunds) ? data.refunds : undefined
  };
};

export default normalizeWlsBookingResponse;
